import { Box, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IPositionType } from 'src/generated-types'
import { ConfigureView } from 'src/generated-types/configure-positions'
import { IChangeType } from 'src/generated-types/project-version-changes'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { CHANGE_GLYPH, CHANGE_LABEL_KEY } from '../../../versions/changeLabels'
import { getChangeColor } from '../../../versions/projectVersionChanges.action'
import { RevisionConfigure } from '../../../versions/useRevisionConfigure'
import { ConfigurePalette } from './configurePalette'

const LIST_HINT_KEYS = ['configure-hint-click', 'configure-hint-shift', 'configure-hint-esc']
const LEGEND_CHANGES: IChangeType[] = [IChangeType.Added, IChangeType.Moved, IChangeType.Modified, IChangeType.Removed]

const getDrawingHintKeys = (activeType: IPositionType) => {
  return [
    'configure-hint-click',
    'configure-hint-drag',
    'configure-hint-wheel',
    'configure-hint-pan',
    'configure-hint-alt',
    ...(activeType !== IPositionType.MooringLine ? ['configure-hint-cage'] : []),
    'configure-hint-esc',
  ]
}

interface DrawingHintsProps {
  view: ConfigureView
  activeType: IPositionType
  palette: ConfigurePalette
  revision?: RevisionConfigure
}

const DrawingHints: React.FC<DrawingHintsProps> = ({ view, activeType, palette, revision }) => {
  const { t } = useTranslation(['position', 'projects'])
  const theme = useTheme()
  const hintKeys = view === 'list' ? LIST_HINT_KEYS : getDrawingHintKeys(activeType)
  const toolHint =
    revision?.view === 'after' && view === 'drawing'
      ? { addLine: 'revision-add-line-hint', remove: 'revision-remove-hint', select: undefined }[revision.tool]
      : undefined
  const showChanges = !!revision?.highlight && view === 'drawing'

  return (
    <>
      <HintRow>
        {toolHint ? (
          <span>{t(toolHint, { ns: 'projects' })}</span>
        ) : (
          hintKeys.map(key => <span key={key}>{t(key, { ns: 'position' })}</span>)
        )}
      </HintRow>
      <HintRow sx={{ gap: '6px 16px' }}>
        {view === 'drawing' && (
          <>
            <LegendItem>
              <LegendLine lineColor={palette.unconfigured} dashed />
              {t('unconfigured', { ns: 'projects' })}
            </LegendItem>
            <LegendItem>
              <LegendLine lineColor={palette.primary} />
              {t('legend-selected', { ns: 'position' })}
            </LegendItem>
          </>
        )}
        {showChanges &&
          LEGEND_CHANGES.map(kind => (
            <LegendItem key={kind}>
              <LegendGlyph sx={{ backgroundColor: getChangeColor(theme, kind) }}>{CHANGE_GLYPH[kind]}</LegendGlyph>
              {t(CHANGE_LABEL_KEY[kind], { ns: 'projects' })}
            </LegendItem>
          ))}
      </HintRow>
    </>
  )
}

export default DrawingHints

const HintRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 14px',
  fontSize: 12,
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const LegendItem = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}))

const LegendLine = styled('i', {
  shouldForwardProp: prop => prop !== 'lineColor' && prop !== 'dashed',
})<{ lineColor: string; dashed?: boolean }>(({ lineColor, dashed }) => ({
  width: 20,
  borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${lineColor}`,
}))

// Same disc and glyph as the change badges on the map.
const LegendGlyph = styled('span')({
  display: 'inline-grid',
  placeItems: 'center',
  width: 16,
  height: 16,
  borderRadius: '50%',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
})
